/* [DISABLED_TEMPORARILY] @ typescript-eslint /camelcase */

import { AxiosResponse, AxiosInstance } from 'axios';
import { TOFDResponse, TOFDTrackingResponse, TOTPResponse } from '../types/ofd/api';
import { TLatLng } from '../types/customer_address';
import { TLocaleAPI } from '../types/locale';

export type TOfdEndpoints = {
    getData: (orderNr: string) => Promise<AxiosResponse<TOFDResponse>>;
    reschedule: (orderNr: string, date: string, slotKey: string) => Promise<AxiosResponse<TOFDResponse>>;
    addressChange: (orderNr: string, addressCode: string) => Promise<AxiosResponse<TOFDResponse>>;
    setCollectionPoint: (orderNr: string, collectionPointCode: string) => Promise<AxiosResponse<TOFDResponse>>;
    pinChange: (orderNr: string, latlng: TLatLng) => Promise<AxiosResponse<TOFDResponse>>;
    setPreference: (
        orderNr: string,
        preference: { isConsolidationRequested: boolean } | { isContactlessRequested: boolean },
    ) => Promise<AxiosResponse<TOFDResponse>>;
    cancel: (orderNr: string, items: { itemNr: string }[]) => Promise<AxiosResponse<TOFDResponse>>;
    track: (orderNr: string, driverID: string) => Promise<AxiosResponse<TOFDTrackingResponse>>;
    requestOTP: (orderNr: string) => Promise<AxiosResponse<TOTPResponse>>;
};

export default (api: AxiosInstance, apiLocale: TLocaleAPI): TOfdEndpoints => {
    const getData = async (orderNr: string): Promise<AxiosResponse<TOFDResponse>> =>
        api.get<TOFDResponse>(`_svc/customer-v1/order-ofd/v2/${orderNr}`, { headers: { 'x-locale': apiLocale } });

    const reschedule = async (orderNr: string, date: string, slotKey: string): Promise<AxiosResponse<TOFDResponse>> =>
        api.post<TOFDResponse>(
            `_svc/customer-v1/order-ofd/v2/${orderNr}/reschedule`,
            {
                scheduled_date: date,
                scheduled_slot_key: slotKey,
            },
            { headers: { 'x-locale': apiLocale } },
        );

    const addressChange = async (orderNr: string, addressCode: string): Promise<AxiosResponse<TOFDResponse>> =>
        api.post<TOFDResponse>(
            `_svc/customer-v1/order-ofd/v2/${orderNr}/address`,
            {
                addressCode,
            },
            { headers: { 'x-locale': apiLocale } },
        );

    const setCollectionPoint = async (
        orderNr: string,
        collectionPointCode: string,
    ): Promise<AxiosResponse<TOFDResponse>> =>
        api.post<TOFDResponse>(
            `_svc/customer-v1/order-ofd/v2/${orderNr}/collection-point`,
            {
                collectionPointCode,
            },
            { headers: { 'x-locale': apiLocale } },
        );

    const pinChange = async (orderNr: string, latlng: TLatLng): Promise<AxiosResponse<TOFDResponse>> =>
        api.post<TOFDResponse>(`_svc/customer-v1/order-ofd/v2/${orderNr}/share-pin`, latlng, {
            headers: { 'x-locale': apiLocale },
        });

    const setPreference = async (
        orderNr: string,
        preference: { isConsolidationRequested: boolean } | { isContactlessRequested: boolean },
    ): Promise<AxiosResponse<TOFDResponse>> =>
        api.post<TOFDResponse>(`_svc/customer-v1/order-ofd/v2/${orderNr}/preference`, preference, {
            headers: { 'x-locale': apiLocale },
        });

    const cancel = async (orderNr: string, items: { itemNr: string }[]): Promise<AxiosResponse<TOFDResponse>> =>
        api.post<TOFDResponse>(
            `_svc/customer-v1/order-ofd/v2/${orderNr}/cancel`,
            { items },
            { headers: { 'x-locale': apiLocale } },
        );

    const track = async (orderNr: string, driverID: string): Promise<AxiosResponse<TOFDTrackingResponse>> =>
        api.get<TOFDTrackingResponse>(`_svc/customer-v1/order-ofd/${orderNr}/${driverID}/track`, {
            headers: { 'x-locale': apiLocale },
        });

    const requestOTP = async (orderNr: string): Promise<AxiosResponse<TOTPResponse>> =>
        api.get<TOTPResponse>(`_svc/customer-v1/order-ofd/otp/${orderNr}`, { headers: { 'x-locale': apiLocale } });

    return {
        track,
        getData,
        reschedule,
        addressChange,
        setCollectionPoint,
        pinChange,
        setPreference,
        cancel,
        requestOTP,
    };
};
